import { SelectItem } from 'primeng/api';

import { DEFAULT_WORKING_HOURS_PER_WEEK } from '@shared/constants';
import {
  DayOfWeek,
  LabourCostMethod,
  Phase,
  PhaseTemplate,
  Product,
  ProductPackage,
  ProductPackageEditItem,
  ProductPackageItem,
  ProductTemplate,
  Project,
  QualityCheckItem,
  StatusSeverity,
  StatusColor,
  TimeSpan,
  WorkUnit,
  WorkUnitDoneQualityCheckItemStatus,
  WorkUnitQaSummary,
  WorkingHours,
  WorkPackage,
  WorkPackageWorkUnit,
} from '@shared/models';

import { getMax, getMin } from './array.utils';
import { FormGenerator } from './form-generator';
import {
  addDays,
  addWeeks,
  fromDayString,
  getDateDifference,
  getDayOfWeekFromDate,
  getDaysBetween,
  getFirstDayOfWeek,
  toDate,
  toDayString,
} from './time.utils';

export const projectProgressColors: SelectItem<StatusColor>[] = [
  { label: 'project.progress.onTrack', value: 'green' },
  { label: 'project.progress.behind', value: 'red' },
  { label: 'project.progress.warning', value: 'yellow' },
];

export const workUnitQaStatusColors: SelectItem<StatusColor>[] = [
  { label: 'project.progress.qaComplete', value: 'green' },
  { label: 'project.progress.qaIncomplete', value: 'red' },
  { label: 'project.progress.qaInProgress', value: 'yellow' },
];

export const statusSeverityByColor: Record<StatusColor, StatusSeverity> = {
  green: 'success',
  red: 'danger',
  yellow: 'warn',
};

export function isWorkUnitFocusFilter(
  filters: {
    progress?: StatusColor[] | null;
    withPlannedEmployeesOnly?: boolean;
    qaColors?: StatusColor[] | null;
  },
  includeQaStatus = true
): boolean {
  return !!(
    filters.withPlannedEmployeesOnly &&
    filters.progress?.includes('red') &&
    filters.progress.includes('yellow') &&
    (!includeQaStatus || (filters.qaColors?.includes('red') && filters.qaColors.includes('yellow')))
  );
}

export function getProjectProgressColor(progress: number, plannedProgress: number): StatusColor {
  if (progress >= plannedProgress) {
    return projectProgressColors[0].value;
  } else if (progress - plannedProgress < -10) {
    return projectProgressColors[1].value;
  } else {
    return projectProgressColors[2].value;
  }
}

export function getWorkUnitQaSummary(workUnit: WorkUnit): WorkUnitQaSummary {
  const qualityCheckLists = workUnit.qualityCheckLists ?? [];
  const doneQualityCheckItems = qualityCheckLists.flatMap(
    (qualityCheckList) => qualityCheckList.doneQualityCheckItems ?? []
  );

  return {
    workUnitId: workUnit.id,
    qualityCheckPicturesCount: workUnit.qualityCheckPictures?.length ?? 0,
    qualityCheckListCount: qualityCheckLists.length,
    qualityCheckDoneItemsCount: doneQualityCheckItems.length,
    qualityCheckDoneApprovedItemsCount: doneQualityCheckItems.filter(
      (item) => item.status === WorkUnitDoneQualityCheckItemStatus.Approved
    ).length,
    qualityCheckDoneNotApprovedItemsCount: doneQualityCheckItems.filter(
      (item) => item.status === WorkUnitDoneQualityCheckItemStatus.NotApproved
    ).length,
  };
}

export function canCompleteWorkUnit(workUnit: WorkUnit, qualityCheckItems: QualityCheckItem[]): boolean {
  const requiredItems = qualityCheckItems.filter(
    (item) =>
      item.activityId === workUnit.activityId &&
      item.qualityCheckItemPhases.some((phase) => phase.phaseId === workUnit.phaseId)
  );

  return (workUnit.qualityCheckLists ?? []).every((checkList) => {
    const doneItems = checkList.doneQualityCheckItems ?? [];

    if (checkList.qualityCheckListTemplateId) {
      const requiredTemplateIds = new Set(
        (checkList.qualityCheckListTemplateItems ?? []).map((item) => item.qualityCheckItemTemplateId)
      );
      const templateDoneItems = doneItems.filter((doneItem) =>
        requiredTemplateIds.has(doneItem.qualityCheckItemTemplateId)
      );
      return (
        [...requiredTemplateIds].every((templateId) =>
          templateDoneItems.some((doneItem) => doneItem.qualityCheckItemTemplateId === templateId)
        ) && templateDoneItems.every((item) => item.status === WorkUnitDoneQualityCheckItemStatus.Approved)
      );
    }

    return (
      requiredItems.every((item) => doneItems.some((doneItem) => doneItem.qualityCheckItemId === item.id)) &&
      doneItems.every((item) => item.status === WorkUnitDoneQualityCheckItemStatus.Approved)
    );
  });
}

export function getWorkUnitQaColors(workUnit: WorkUnit): StatusColor[] {
  return getWorkUnitQaColorsFromSummary(getWorkUnitQaSummary(workUnit));
}

export function getWorkUnitQaColorsFromSummary(qaSummary: WorkUnitQaSummary): StatusColor[] {
  const colors = new Set<StatusColor>();

  if (qaSummary.qualityCheckPicturesCount > 0) {
    colors.add('green');
  }

  if (qaSummary.qualityCheckListCount > 0) {
    if (qaSummary.qualityCheckDoneNotApprovedItemsCount > 0 || qaSummary.qualityCheckDoneItemsCount === 0) {
      colors.add('red');
    } else if (qaSummary.qualityCheckDoneApprovedItemsCount === qaSummary.qualityCheckDoneItemsCount) {
      colors.add('green');
    } else {
      colors.add('yellow');
    }
  }

  return [...colors];
}

export function getWorkPackageWorkUnits(workPackage: WorkPackage): WorkPackageWorkUnit[] {
  // INFO (Denis) - the work units of the package and of all its child packages
  return [
    ...(workPackage.workUnits ?? []),
    ...(workPackage.childWorkPackages ?? []).flatMap((child) => getWorkPackageWorkUnits(child)),
  ];
}

export function getProjectWeeks(project: Project | undefined): string[] {
  if (!project) {
    return [];
  }

  return getWeeksBetween(project.details.startDate, project.details.endDate);
}

export function getProjectsWeeks(projects: Project[]): string[] {
  return getWeeksBetween(
    getMin(projects, (p) => (p?.details?.startDate ? toDate(p?.details?.startDate) : null)),
    getMax(projects, (p) => (p?.details?.endDate ? toDate(p?.details?.endDate) : null))
  );
}

export function getProjectWorkingDaysOfWeek(project: Project | undefined, weekStartDay?: string): DayOfWeek[] {
  const workingHours = getProjectWorkingHours(project, weekStartDay);

  return workingHours ? getWorkingDaysOfWeek(workingHours) : [];
}

export function getProjectWorkingHoursForDay(
  project?: Project,
  day?: DayOfWeek,
  weekStartDay?: string
): {
  startsAt?: string;
  endsAt?: string;
  pauseStartsAt?: string;
  pauseEndsAt?: string;
} | null {
  if (!project || day === null) return null;

  const workingHours = getProjectWorkingHours(project, weekStartDay);
  const keys = getWorkingHoursDayKeys(day);

  return {
    startsAt: getWorkingHoursValue(workingHours, keys.startsAt),
    endsAt: getWorkingHoursValue(workingHours, keys.endsAt),
    pauseStartsAt: getWorkingHoursValue(workingHours, keys.pauseStartsAt),
    pauseEndsAt: getWorkingHoursValue(workingHours, keys.pauseEndsAt),
  };
}

export function getProjectWorkingDaysForWeekOfDate(project: Project | undefined, date: Date | undefined): DayOfWeek[] {
  if (!date) {
    return getProjectWorkingDaysOfWeek(project);
  }

  const weekStartDay = toDayString(getFirstDayOfWeek(date));
  return getProjectWorkingDaysOfWeek(project, weekStartDay);
}

export function getProjectDurationWorkingDays(
  project: Project | undefined,
  startDate: Date | undefined,
  endDate: Date | undefined
): number | undefined {
  if (!startDate || !endDate) {
    return undefined;
  }

  let currentDate = toDate(startDate);
  const lastDate = toDate(endDate);
  let durationDays = 0;

  while (currentDate <= lastDate) {
    if (isProjectWorkingDay(project, currentDate)) {
      durationDays++;
    }

    currentDate = addDays(currentDate, 1);
  }

  return durationDays;
}

export function addProjectWorkingDays(project: Project | undefined, startDate: Date, durationDays: number): Date {
  if (durationDays <= 1) {
    return toDate(startDate);
  }

  let currentDate = toDate(startDate);
  let remainingWorkingDays = durationDays;
  let safetyCounter = 0;

  while (remainingWorkingDays > 0 && safetyCounter < 5000) {
    if (isProjectWorkingDay(project, currentDate)) {
      remainingWorkingDays--;

      if (remainingWorkingDays === 0) {
        return currentDate;
      }
    }

    currentDate = addDays(currentDate, 1);
    safetyCounter++;
  }

  return addDays(toDate(startDate), durationDays - 1);
}

export function getProjectHoursInWeekForOneEmployee(project: Project | undefined, week: string): number {
  const workingHours = getProjectWorkingHours(project, week);
  if (!workingHours?.netWorkingHoursPerWeek) {
    return DEFAULT_WORKING_HOURS_PER_WEEK;
  }

  const netWorkingHoursPerWeek =
    new TimeSpan(workingHours.netWorkingHoursPerWeek).totalHours() ?? DEFAULT_WORKING_HOURS_PER_WEEK;
  const holidayHours = getDaysBetween(week, addDays(fromDayString(week), 6))
    .filter((day) => isProjectHoliday(project, day))
    .reduce((sum, day) => sum + getNetWorkingHoursOfDay(workingHours, getDayOfWeekFromDate(day)), 0);

  return Math.max(netWorkingHoursPerWeek - holidayHours, 0);
}

export function getDayOfWeekOrder(day: DayOfWeek): number {
  return day === DayOfWeek.Sunday ? 7 : day;
}

export function isProductPackageParent(product: Product): boolean {
  return !!product.packageAssignmentGuid && product.isPackageParent;
}

export function isProductPackageChild(product: Product): boolean {
  return !!product.packageAssignmentGuid && !product.isPackageParent;
}

export function buildPackageParentProduct(
  project: Project,
  productPackage: ProductPackage,
  assignmentGuid: string,
  phaseId: string,
  activityId: string,
  order: number
): Product {
  return FormGenerator.initProductForm(
    undefined,
    {
      packageAssignmentGuid: assignmentGuid,
      isPackageParent: true,
      sourcePackageId: productPackage.isProjectAssignment ? undefined : productPackage.id,
      sourcePackageGuid: productPackage.id,
      sourcePackageName: productPackage.name,
      sourcePackageCode: productPackage.code,
      packageItemQuantity: undefined,
      phaseId: phaseId,
      activityId: activityId,
      name: productPackage.name,
      referenceCode: productPackage.code,
      unitOfMeasure: productPackage.unitOfMeasure,
      labourCostMethod: LabourCostMethod.NoInstallationCosts,
      quantityDocumentation: productPackage.quantity,
    } as unknown as Product,
    project,
    phaseId,
    activityId,
    order,
    true
  ).getRawValue();
}

export function convertToPackageParentProduct(product: Product, assignmentGuid: string): Product {
  return {
    ...product,
    packageAssignmentGuid: assignmentGuid,
    isPackageParent: true,
    sourcePackageId: undefined,
    sourcePackageGuid: undefined,
    sourcePackageName: undefined,
    sourcePackageCode: undefined,
    sourcePackageItemId: undefined,
    packageItemQuantity: undefined,
  };
}

export function convertFromPackageParentProduct(product: Product): Product {
  return {
    ...product,
    packageAssignmentGuid: undefined,
    isPackageParent: false,
    sourcePackageId: undefined,
    sourcePackageGuid: undefined,
    sourcePackageName: undefined,
    sourcePackageCode: undefined,
    sourcePackageItemId: undefined,
    packageItemQuantity: undefined,
  };
}

export function buildPackageChildProduct(
  project: Project,
  packageItem: ProductPackageItem,
  productTemplate: ProductTemplate,
  assignmentId: string,
  phaseId: string,
  activityId: string,
  order: number,
  sourcePackageId?: string,
  sourcePackageGuid?: string,
  sourcePackageName?: string,
  sourcePackageCode?: string,
  sourcePackageItemId?: string
): Product {
  return FormGenerator.initProductForm(
    undefined,
    {
      ...productTemplate,
      packageAssignmentGuid: assignmentId,
      sourcePackageId,
      sourcePackageGuid,
      sourcePackageName,
      sourcePackageCode,
      sourcePackageItemId,
      packageItemQuantity: packageItem.quantity,
      referenceCode: packageItem.referenceCode ?? productTemplate?.referenceCode,
      name: packageItem.name ?? productTemplate?.name,
      productTemplateId: packageItem.productTemplateId,
      specialTreatmentCosts: packageItem.productTemplateId
        ? productTemplate?.specialTreatmentCosts
        : packageItem.specialTreatmentCosts,
      labourCostMethod: packageItem.productTemplateId
        ? productTemplate?.labourCostMethod
        : packageItem.labourCostMethod,
      baseSalaryPerUnit: packageItem.productTemplateId
        ? productTemplate?.baseSalaryPerUnit
        : packageItem.baseSalaryPerUnit,
      baseUnitTime: packageItem.productTemplateId ? productTemplate?.baseUnitTime : packageItem.baseUnitTime,
      unitCost: packageItem.productTemplateId ? productTemplate?.unitCost : packageItem.unitCost,
      additionalCostsPercentage: packageItem.productTemplateId
        ? productTemplate?.additionalCostsPercentage
        : packageItem.additionalCostsPercentage,
      additionalSubcontractorCosts: packageItem.productTemplateId
        ? productTemplate?.additionalSubcontractorCosts
        : packageItem.additionalSubcontractorCosts,
      additionalLabourCostsPercentage: packageItem.productTemplateId
        ? productTemplate?.additionalLabourCostsPercentage
        : packageItem.additionalLabourCostsPercentage,
      phaseId: phaseId,
      activityId: activityId,
    } as unknown as Product,
    project,
    phaseId,
    activityId,
    order,
    true
  ).getRawValue();
}

export function initProductPackageAssignmentRows(
  productPackage: ProductPackage,
  phaseTemplates: PhaseTemplate[],
  project: Project,
  defaultPhaseId: string
): ProductPackageEditItem[] {
  return productPackage.allCombinedItems.map((item) => ({
    item,
    phaseId: findPhaseForPhaseTemplate(item.phaseTemplateId, phaseTemplates, project, defaultPhaseId)?.id,
    quantity: item.quantity ?? 1,
    sourcePackageId: !productPackage.isProjectAssignment ? productPackage.id : undefined,
    sourcePackageGuid: productPackage.id,
    sourcePackageName: productPackage.name,
    sourcePackageCode: productPackage.code,
    sourcePackageItemId: item.id,
  }));
}

export function findPhaseForPhaseTemplate(
  phaseTemplateId: string,
  phaseTemplates: PhaseTemplate[],
  project: Project,
  defaultPhaseId: string
): Phase | undefined {
  const phaseTemplate = phaseTemplates.find((pt) => pt.id === phaseTemplateId);
  if (phaseTemplate) {
    const foundPhase = project.phases?.find((phase) => phase.name?.toLowerCase() === phaseTemplate.name.toLowerCase());
    if (foundPhase) {
      return foundPhase;
    }
  }

  return project.phases.find((phase) => phase.id === defaultPhaseId);
}

function getWorkingDaysOfWeek(workingHours: WorkingHours): DayOfWeek[] {
  return workingHoursDayKeys
    .filter(
      (item) =>
        workingHours[item.startsAt] &&
        workingHours[item.endsAt] &&
        (new TimeSpan(workingHours[item.startsAt] as string).totalSeconds() > 0 ||
          new TimeSpan(workingHours[item.endsAt] as string).totalSeconds() > 0)
    )
    .map((item) => item.day)
    .sort((a, b) => getDayOfWeekOrder(a) - getDayOfWeekOrder(b));
}

function isProjectWorkingDay(project: Project | undefined, date: Date): boolean {
  return (
    getProjectWorkingDaysForWeekOfDate(project, date).includes(getDayOfWeekFromDate(date)) &&
    !isProjectHoliday(project, toDayString(date))
  );
}

function isProjectHoliday(project: Project | undefined, day: string): boolean {
  return project?.holidays?.some((holiday) => holiday.from <= day && day <= holiday.to) ?? false;
}

function getNetWorkingHoursOfDay(workingHours: WorkingHours, day: DayOfWeek): number {
  const keys = getWorkingHoursDayKeys(day);

  return TimeSpan.getTotalHours(
    getWorkingHoursValue(workingHours, keys.startsAt),
    getWorkingHoursValue(workingHours, keys.endsAt),
    getWorkingHoursValue(workingHours, keys.pauseStartsAt),
    getWorkingHoursValue(workingHours, keys.pauseEndsAt)
  ).totalHours();
}

function getProjectWorkingHours(project: Project | undefined, weekStartDay?: string): WorkingHours | undefined {
  return (
    project?.workingHours?.find((item) => item.weekStartDay === weekStartDay) ??
    project?.workingHours?.find((item) => !item.weekStartDay) ??
    project?.defaultWorkingHours
  );
}

function getWorkingHoursValue(workingHours: WorkingHours | undefined, key: keyof WorkingHours): string | null {
  const value = workingHours?.[key];
  return typeof value === 'string' && value ? value : null;
}

function getWorkingHoursDayKeys(day: DayOfWeek): {
  day: DayOfWeek;
  startsAt: keyof WorkingHours;
  endsAt: keyof WorkingHours;
  pauseStartsAt: keyof WorkingHours;
  pauseEndsAt: keyof WorkingHours;
} {
  return workingHoursDayKeys.find((item) => item.day === day)!;
}

const workingHoursDayKeys: {
  day: DayOfWeek;
  startsAt: keyof WorkingHours;
  endsAt: keyof WorkingHours;
  pauseStartsAt: keyof WorkingHours;
  pauseEndsAt: keyof WorkingHours;
}[] = [
  {
    day: DayOfWeek.Monday,
    startsAt: 'mondayStartsAt',
    endsAt: 'mondayEndsAt',
    pauseStartsAt: 'mondayPauseStartsAt',
    pauseEndsAt: 'mondayPauseEndsAt',
  },
  {
    day: DayOfWeek.Tuesday,
    startsAt: 'tuesdayStartsAt',
    endsAt: 'tuesdayEndsAt',
    pauseStartsAt: 'tuesdayPauseStartsAt',
    pauseEndsAt: 'tuesdayPauseEndsAt',
  },
  {
    day: DayOfWeek.Wednesday,
    startsAt: 'wednesdayStartsAt',
    endsAt: 'wednesdayEndsAt',
    pauseStartsAt: 'wednesdayPauseStartsAt',
    pauseEndsAt: 'wednesdayPauseEndsAt',
  },
  {
    day: DayOfWeek.Thursday,
    startsAt: 'thursdayStartsAt',
    endsAt: 'thursdayEndsAt',
    pauseStartsAt: 'thursdayPauseStartsAt',
    pauseEndsAt: 'thursdayPauseEndsAt',
  },
  {
    day: DayOfWeek.Friday,
    startsAt: 'fridayStartsAt',
    endsAt: 'fridayEndsAt',
    pauseStartsAt: 'fridayPauseStartsAt',
    pauseEndsAt: 'fridayPauseEndsAt',
  },
  {
    day: DayOfWeek.Saturday,
    startsAt: 'saturdayStartsAt',
    endsAt: 'saturdayEndsAt',
    pauseStartsAt: 'saturdayPauseStartsAt',
    pauseEndsAt: 'saturdayPauseEndsAt',
  },
  {
    day: DayOfWeek.Sunday,
    startsAt: 'sundayStartsAt',
    endsAt: 'sundayEndsAt',
    pauseStartsAt: 'sundayPauseStartsAt',
    pauseEndsAt: 'sundayPauseEndsAt',
  },
];

export function getWeeksBetween(startDate: Date, endDate: Date): string[] {
  const projectWeeksCount = Math.round((getDateDifference(startDate, endDate).asWeeks() || 0) + 1);

  const projectWeeks = new Array(projectWeeksCount).fill(null).map((_, i) => i);

  return projectWeeks.map((index) => {
    const date = addWeeks(startDate, index);
    const startWeekDate = getFirstDayOfWeek(date);
    return toDayString(startWeekDate);
  });
}
